import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../../../lib/axios';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../../GlobalFunction/userPermissions';
import { Box, Chip } from '@mui/material';
import FilterTable from '../../../../atoms/TableComponent/FilterTable/FilterTable';
import { toast } from 'react-toastify';

export default function HkWorkerTable() {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();

  // ---- NEW: mobile tap handling -------------------------------------------------
  const [tappedRowId, setTappedRowId] = useState(null);
  const [lastTap, setLastTap] = useState(0);
  const DOUBLE_TAP_DELAY = 300; // ms

  const isMobile = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const permissions = usePermissions("Worker Master");
  const { AddRec = "0" } = permissions || {};

  useEffect(() => {
    fetchHkTypeData();
  }, []);

  const fetchHkTypeData = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post('MstHkWrkr/getAllMstHkWrkrDashBoard', {
        "start": 0,
        "PageSize": 0,
        "SearchText": ""
      });
      const { data: { Status, Data, Message } } = response;
      if (Status === 0 && Array.isArray(Data)) {
        const formattedData = Data.map(hkType => ({
          ...hkType,
          RowNum: hkType.RowNum || '',
          HkWrkrName: hkType.HkWrkrName || '',
          HkWrkTypeName: hkType.HkWrkTypeName || '',
          Abrv: hkType.Abrv || '',
          Remark: hkType.Remark || '',
          Status: hkType.Status === null ? '' : (hkType.Status == "1" ? 'Active' : 'Inactive'),
        }));
        setRows(formattedData);
      }
    } catch (error) {
      toast.error('Error fetching housekeeping type data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCellClicked = useCallback(
    (params) => {
      if (!params.data?.HkWrkrId) return;

      const id = params.data.HkWrkrId;

      // visual feedback
      setTappedRowId(id);
      setTimeout(() => setTappedRowId(null), 300);

      const now = Date.now();
      const timeSince = now - lastTap;

      // Mobile → single tap
      if (isMobile()) {
        navigate('/hk-worker', { state: { HkWrkrId: id, mode: 'view' } });
        return;
      }

      // Desktop → double-click (fallback for onRowDoubleClicked)
      if (timeSince > 0 && timeSince < DOUBLE_TAP_DELAY) {
        navigate('/hk-worker', { state: { HkWrkrId: id, mode: 'view' } });
      }
      setLastTap(now);
    },
    [navigate, lastTap]
  );

  const getRowStyle = (params) => {
    if (tappedRowId === params.data?.HkWrkrId) {
      return { background: '#bbdefb', transition: 'background 0.2s' };
    }
    return null;
  };

  const handlePermittedClick = () => {
    if (AddRec === "1") {
      handleClick();
    }
  };

  const handleClick = () => {
    navigate('/hk-worker', { state: { mode: "add" } });
  };

  const handleHomeClick = () => {
    navigate('/dashboard');
  };

  const handleLocationclick = () => {
    navigate('/masters/operations');
  };

  const handleRowDoubleClick = (params) => {
    const hkWrkId = params?.data?.HkWrkrId;
    if (hkWrkId) {
      navigate('/hk-worker', { state: { HkWrkrId: hkWrkId, mode: 'view' } });
    }
  };

  const TruncatedCellRenderer = (props) => {
    const [isOverflowed, setIsOverflowed] = useState(false);
    const cellRef = React.useRef(null);

    useEffect(() => {
      if (cellRef.current) {
        const isTextOverflowed =
          cellRef.current.scrollWidth > cellRef.current.clientWidth ||
          (props.value && props.value.length > 20);
        setIsOverflowed(isTextOverflowed);
      }
    }, [props.value]);

    const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

    return (
      <div
        ref={cellRef}
        className="truncated-cell"
        style={{ width: '100%' }}
      >
        {cellValue}
      </div>
    );
  };

  const StatusCellRenderer = (props) => {
    const getStatusColor = (status) => {
      switch (status) {
        case 'Active':
          return '#4caf50';
        case 'InActive':
          return '#f44336';
        default:
          return '#757575';
      }
    };

    return (
      <Chip
        label={props.value}
        className="status-chip"
        sx={{
          backgroundColor: getStatusColor(props.value),
          color: 'white',
          width: '100%',
          maxWidth: '100px',
        }}
      />
    );
  };

  const columnDefs = [
    { headerName: 'Sr.No.', field: 'RowNum', width: 60, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Doc No.', field: 'HkWrkrNo', width: 120, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Worker Name', field: 'HkWrkrName', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Worker Type Name', field: 'HkWrkTypeName', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Service Provider', field: 'VendName', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Abrv', field: 'Abrv', minWidth: 180, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Guardian', field: 'GuardianNm', minWidth: 180, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Adhar No', field: 'AdharNo', minWidth: 140, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Pan No', field: 'PanNo', minWidth: 110, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Mobile No', field: 'MobileNo', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Email', field: 'Email', minWidth: 150, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'RefBy', field: 'RefBy', minWidth: 100, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Remark', field: 'Remark', minWidth: 200, flex: 1, filter: 'agTextColumnFilter', cellRenderer: TruncatedCellRenderer },
    { headerName: 'Status', field: 'Status', width: 100, filter: 'agTextColumnFilter', cellRenderer: StatusCellRenderer },
  ];

  return (
    <Box sx={{ height: '90vh' }}>
      <FilterTable
        columnDefs={columnDefs}
        rowData={rows}
        onRowDoubleClicked={handleRowDoubleClick}
        isLoading={isLoading}
        rowSelection="single"
        onHomeClick={handleHomeClick}
        onParentClick={handleLocationclick}
        breadcrumbParentLabel="Operations"
        breadcrumbCurrentLabel="Worker Master"
        onAddClick={handlePermittedClick}
        hasAddPermission={AddRec === "1"}
        onCellClicked={handleCellClicked}
        getRowStyle={getRowStyle}
      />
    </Box>
  );
}
